import { api } from '@/api';
import { AIRuleResponse } from '@polybank/shared/types/ai-contracts';
import type { AIUsageSnapshot } from '@polybank/shared';

export interface GenerateRuleParams {
  description: string;
  amount: number;
  date: string;
  customPrompt?: string;
  categoryName?: string;
}

export interface GenerateRuleResponse {
  data: AIRuleResponse;
}

export interface GenerateQueryResponse {
  query: string;
}

/**
 * Service for AI-related API operations
 */
class AiService {
  /**
   * Get current AI usage for this billing period
   */
  async getUsage(): Promise<AIUsageSnapshot | null> {
    const result = await api.v1.ai.usage.get();
    if (result.error) return null;
    return (result.data as { success: boolean; data: AIUsageSnapshot }).data;
  }

  /**
   * Generate a rule from transaction details using AI
   */
  async generateRule(params: GenerateRuleParams) {
    const result = await api.v1.ai['generate-rule'].post(params);
    if (result.error) {
      throw result.error.value;
    }
    return result.data as GenerateRuleResponse;
  }

  /**
   * Generate a query from natural language
   */
  async generateQuery(naturalLanguageQuery: string) {
    const result = await api.v1.ai['generate-query'].post({
      query: naturalLanguageQuery,
    });
    if (result.error) {
      throw result.error.value;
    }
    return result;
  }

  async bulkCategorize(signal?: AbortSignal) {
    return api.v1.ai['bulk-categorize'].post(undefined, { fetch: { signal } });
  }

  async bulkGenerateRules(signal?: AbortSignal) {
    return api.v1.ai['bulk-generate-rules'].post(undefined, {
      fetch: { signal },
    });
  }

  async hybridCategorize(forceAll?: boolean, signal?: AbortSignal) {
    return api.v1.ai['hybrid-categorize'].post(
      { forceAll: forceAll ?? false },
      { fetch: { signal } }
    );
  }

  async executeBulkRule(
    id: string,
    raw: AIRuleResponse,
    title: string,
    humanReadable: string
  ) {
    return api.v1.ai['execute-bulk-rule'].post({
      id,
      raw,
      title,
      humanReadable,
    });
  }
}

export const aiService = new AiService();
